import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import useCreateJobAdSubCollectionDoc from "@/hooks/mutations/use-create-job-ad-sub-collection-doc";
import useUpdateMatchReviewStatus from "@/hooks/mutations/use-update-match-review-status";
import { ReviewStatus } from "@/types/topLevelReciprocalMatch";
import { useProfile } from "@/context/ProfileContext";
import { isFeatureEnabled } from "@/lib/featureFlags";

export default function MatchActionsDialog({
  candidate,
  selectedAdId,
  setSelectedMatch,
  refreshUsers,
}: {
  candidate: any;
  selectedAdId: string;
  setSelectedMatch: (match: any) => void;
  refreshUsers: (callback: (prevCount: number) => number) => void; //
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { memberProfile } = useProfile();

  const { mutateAsync: createJobAdSubCollectionDoc } =
    useCreateJobAdSubCollectionDoc();
  const { mutateAsync: updateMatchReviewStatus } = useUpdateMatchReviewStatus();

  const handleBusinessQuitProcessWithoutUser = async () => {
    try {
      await createJobAdSubCollectionDoc({
        subCollection: "BusinessQuitProcessWithUser",
        jobAdId: selectedAdId,
        candidateUid: candidate.uid,
      });
      // await setDoc(
      //   doc(
      //     db,
      //     `JobAds/${selectedAdId}/BusinessQuitProcessWithUser`,
      //     candidate.uid
      //   ),
      //   {
      //     timestamp: Timestamp.now(),
      //     uid: candidate.uid,
      //   }
      // );
      refreshUsers((prevCount) => prevCount + 1);
      setSelectedMatch(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleUserQuitProcess = async () => {
    try {
      await createJobAdSubCollectionDoc({
        subCollection: "UsersQuitProcess",
        jobAdId: selectedAdId,
        candidateUid: candidate.uid,
      });
      // await setDoc(
      //   doc(db, `JobAds/${selectedAdId}/UsersQuitProcess`, candidate.uid),
      //   {
      //     timestamp: Timestamp.now(),
      //     uid: candidate.uid,
      //   }
      // );

      await createJobAdSubCollectionDoc({
        subCollection: "BusinessSeenUsersQuitProcess",
        jobAdId: selectedAdId,
        candidateUid: candidate.uid,
      });

      // await setDoc(
      //   doc(
      //     db,
      //     `JobAds/${selectedAdId}/BusinessSeenUsersQuitProcess`,
      //     candidate.uid
      //   ),
      //   {
      //     timestamp: Timestamp.now(),
      //     uid: candidate.uid,
      //   }
      // );
      /*  await setDoc(
        doc(db, `TopLevelReciprocalMatches/${candidate.matchId}`),
        {
          status: "candidateDeclined",
        },
        { merge: true }
      ); */
      refreshUsers((prevCount) => prevCount + 1);
      setSelectedMatch(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleBusinessHiredCandidate = async () => {
    try {
      await createJobAdSubCollectionDoc({
        subCollection: "BusinessHiredCandidate",
        jobAdId: selectedAdId,
        candidateUid: candidate.uid,
      });

      // await setDoc(
      //   doc(db, `JobAds/${selectedAdId}/BusinessHiredCandidate`, candidate.uid),
      //   {
      //     timestamp: Timestamp.now(),
      //     uid: candidate.uid,
      //   }
      // );

      /*  await setDoc(
        doc(db, `TopLevelReciprocalMatches/${candidate.matchId}`),
        {
          status: "hired",
        },
        { merge: true }
      ); */
      refreshUsers((prevCount) => prevCount + 1);
      setSelectedMatch(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleUpdateReviewStatus = async (reviewStatus: ReviewStatus) => {
    try {
      await updateMatchReviewStatus({
        matchId: candidate.matchId,
        reviewStatus,
      });
      toast({
        title:
          reviewStatus === "marked_for_review"
            ? "Kandidaten har markerats för granskning"
            : reviewStatus === "not_for_review"
            ? "Kandidaten har markerats som inte för granskning"
            : "Granskningsstatus har uppdaterats",
      });
      refreshUsers((prevCount) => prevCount + 1);
    } catch (e) {
      console.log(e);
      toast({
        title: "Ett fel uppstod",
        description: "Kunde inte uppdatera granskningsstatus",
        variant: "destructive",
      });
    }
  };

  const handleAction = async (action: string) => {
    setIsLoading(true);
    try {
      switch (action) {
        case "end":
          await handleBusinessQuitProcessWithoutUser();
          break;
        case "candidate-ended":
          await handleUserQuitProcess();
          break;
        case "hired":
          await handleBusinessHiredCandidate();
          break;
      }
      toast({
        title: "Chatten har arkiverats",
      });
      navigate(`/chat/${selectedAdId}`);
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
      setConfirmAction(null);
    }
  };

  const getActionDetails = (action: string) => {
    switch (action) {
      case "end":
        return {
          title: "Avsluta processen",
          description:
            "Är du säker på att du vill avsluta processen? Detta kommer att arkivera chatten och avsluta all kommunikation med kandidaten.",
          confirmText: "Ja, avsluta processen",
        };
      case "hired":
        return {
          title: "Bekräfta anställning",
          description:
            "Är du säker på att kandidaten har blivit anställd? Detta kommer att arkivera chatten och markera processen som slutförd.",
          confirmText: "Ja, bekräfta anställning",
        };
      default:
        return {
          title: "",
          description: "",
          confirmText: "",
        };
    }
  };

  const actionDetails = confirmAction ? getActionDetails(confirmAction) : null;

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          <Button
            variant="destructive"
            onClick={() => setConfirmAction("end")}
            disabled={isLoading}
          >
            Avsluta processen
          </Button>
          <Button
            variant="default"
            onClick={() => setConfirmAction("hired")}
            disabled={isLoading}
          >
            Kandidaten har blivit anställd
          </Button>
        </div>
        {memberProfile?.isSuperAdmin && isFeatureEnabled("SEND_FOR_REVIEW") && (
          <div className="flex flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => handleUpdateReviewStatus("marked_for_review")}
              disabled={isLoading}
              className="text-orange-600 border-orange-600 hover:bg-orange-50"
            >
              Markera för granskning
            </Button>
            <Button
              variant="outline"
              onClick={() => handleUpdateReviewStatus("not_for_review")}
              disabled={isLoading}
              className="text-gray-600 border-gray-600 hover:bg-gray-50"
            >
              Inte för granskning
            </Button>
            <Button
              variant="outline"
              onClick={() => handleUpdateReviewStatus("pending")}
              disabled={isLoading}
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              Återställ status
            </Button>
          </div>
        )}
      </div>

      <Dialog
        open={!!confirmAction}
        onOpenChange={() => setConfirmAction(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionDetails?.title}</DialogTitle>
            <DialogDescription>{actionDetails?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={isLoading}
            >
              Avbryt
            </Button>
            <Button
              variant={confirmAction === "end" ? "destructive" : "default"}
              onClick={() => handleAction(confirmAction!)}
              disabled={isLoading}
            >
              {isLoading ? "Bearbetar..." : actionDetails?.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
